import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { NetworkStatusPillVariants } from "#hooks/adapters/useNetworks";
import { NetworkDetailRoute } from "#routes/organization/networks/[networkId]/detail";
import useLocalization from "#src/hooks/useLocalization";
import {
  HeaderType,
  Pill,
  TextDataDisplay,
  type ItemBaseType,
} from "@validereinc/common-components";
import { AssetGroupStatusType, AssetGroupType } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import { get } from "es-toolkit/compat";
import React, { useMemo } from "react";

export const NETWORK_ATTRIBUTES = {
  ID: {
    key: "id",
    label: "ID",
  },
  NAME: {
    key: "name",
    label: "Network",
  },
  STATUS: {
    key: "status",
    label: "Status",
  },
  REPORTING_GROUP: {
    key: "default_network_reporting_group_id",
    label: "Reporting Scenario",
  },
  CREATED_AT: {
    key: "created_at",
    label: "Created At",
  },
  CREATED_BY: {
    key: "created_by",
    label: "Created By",
  },
  UPDATED_AT: {
    key: "updated_at",
    label: "Updated At",
  },
  UPDATED_BY: {
    key: "updated_by",
    label: "Updated By",
  },
};

export const useNetworkHeaders = <
  ItemType extends ItemBaseType = AssetGroupType,
>({
  prefix,
  keySubstitutions,
  sortSubstitutions,
  reportingGroupMap,
  groupable = false,
}: {
  prefix?: string;
  keySubstitutions?: Record<string, string>;
  sortSubstitutions?: Record<string, string>;
  reportingGroupMap?: Record<string, string>;
  groupable?: boolean;
} = {}): { isLoading: boolean; headers: Array<HeaderType<ItemType>> } => {
  const { localize, isLoading: isLoadingLocalization } = useLocalization();

  const withSortSubstitution = (key: string) =>
    sortSubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);
  const withKeySubstitution = (key: string) =>
    keySubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);

  const headers = useMemo<Array<HeaderType<ItemType>>>(
    () => [
      {
        key: withSortSubstitution(NETWORK_ATTRIBUTES.NAME.key),
        label: localize(NETWORK_ATTRIBUTES.NAME.label),
        isSortable: true,
        renderComponent: ({ item, opts }) => {
          const id = get(item, withKeySubstitution(NETWORK_ATTRIBUTES.ID.key));
          const name = get(
            item,
            withKeySubstitution(NETWORK_ATTRIBUTES.NAME.key)
          );
          const link = NetworkDetailRoute.toLink({
            pathParams: { networkId: id },
          });

          return name && id ? (
            <RoutingLink to={link}>{name}</RoutingLink>
          ) : (
            (opts?.emptyText ?? "-")
          );
        },
      },
      {
        key: withSortSubstitution(NETWORK_ATTRIBUTES.STATUS.key),
        label: NETWORK_ATTRIBUTES.STATUS.label,
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item }: { item: ItemType }) => {
          const status = get(
            item,
            withKeySubstitution(NETWORK_ATTRIBUTES.STATUS.key)
          ) as AssetGroupStatusType;
          return (
            <Pill variant={NetworkStatusPillVariants[status]}>
              {toTitleCaseString(status, {
                intent: "ui",
                exceptions: ["oil-and-gas"],
              })}
            </Pill>
          );
        },
      },
      {
        key: withSortSubstitution(NETWORK_ATTRIBUTES.REPORTING_GROUP.key),
        label: NETWORK_ATTRIBUTES.REPORTING_GROUP.label,
        isSortable: true,
        renderComponent: ({ item, opts }) => {
          const reportingGroup = get(
            item,
            withKeySubstitution(NETWORK_ATTRIBUTES.REPORTING_GROUP.key)
          );

          return (
            <TextDataDisplay
              value={reportingGroupMap?.[reportingGroup]}
              {...opts}
            />
          );
        },
      },
    ],
    [
      withSortSubstitution,
      withKeySubstitution,
      localize,
      reportingGroupMap,
      groupable,
    ]
  );

  return {
    isLoading: isLoadingLocalization,
    headers,
  };
};
